import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Modal, Button, ListGroup } from 'react-bootstrap';
import axios from 'axios';
import {API_URL, STRIPE_PUBLISH_KEY} from '../../constants';
import { AuthContext } from '../AuthContext';
import CardForm from '../CardForm';
import Spinner from "react-bootstrap/Spinner";
import withAuth from "../../auth";
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import {useNavigate} from "react-router-dom";

const stripePromise = loadStripe(`${STRIPE_PUBLISH_KEY}`);

const CreditCardModal = ({
    userId,
    pickupId,
    show,
    cardPaymentAllowed,
    handleClose,
    handleSuccess,
}) => {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(false);
    const { token, setToken } = useContext(AuthContext);
    const navigate = useNavigate();
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const paymentMethodsRequestIdRef = useRef(0);
    const bookingRequestIdRef = useRef(0);
    const currentContextRef = useRef({
        show,
        userId,
        pickupId,
        cardPaymentAllowed,
    });
    currentContextRef.current = {
        show,
        userId,
        pickupId,
        cardPaymentAllowed,
    };

    const fetchPaymentMethods = useCallback(async () => {
        if (!cardPaymentAllowed || !show || !userId) return;

        const requestId = ++paymentMethodsRequestIdRef.current;
        const requestedUserId = userId;
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${API_URL}/api/admin/users/${requestedUserId}/payment-methods`, {
                headers: {'Authorization': `Bearer ${token}`}
            });

            const current = currentContextRef.current;
            if (
                requestId !== paymentMethodsRequestIdRef.current
                || !current.show
                || !current.cardPaymentAllowed
                || String(current.userId) !== String(requestedUserId)
            ) return;

            setPaymentMethods(Array.isArray(response.data?.data) ? response.data.data : []);
        } catch (requestError) {
            const current = currentContextRef.current;
            if (
                requestId !== paymentMethodsRequestIdRef.current
                || !current.show
                || !current.cardPaymentAllowed
                || String(current.userId) !== String(requestedUserId)
            ) return;

            if (requestError.response && requestError.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }
            if (requestError?.response?.data?.errors) {
                const errors = requestError.response.data.errors;
                const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                setError(errorMessages);
            } else if (requestError?.response?.data?.message) {
                setError(requestError.response.data.message);
            } else {
                setError('An error occurred. Please try again later.');
            }
        } finally {
            const current = currentContextRef.current;
            if (
                requestId === paymentMethodsRequestIdRef.current
                && current.show
                && current.cardPaymentAllowed
                && String(current.userId) === String(requestedUserId)
            ) {
                setLoading(false);
            }
        }
    }, [cardPaymentAllowed, navigate, setToken, show, token, userId]);

    useEffect(() => {
        paymentMethodsRequestIdRef.current += 1;
        bookingRequestIdRef.current += 1;
        setSelectedPaymentMethodId(null);
        setPaymentMethods([]);
        setError('');
        setLoading(false);
        setIsSubmitted(false);
        setSuccess('');

        if (show && cardPaymentAllowed && userId) {
            fetchPaymentMethods();
        }

        return () => {
            paymentMethodsRequestIdRef.current += 1;
            bookingRequestIdRef.current += 1;
        };
    }, [cardPaymentAllowed, fetchPaymentMethods, pickupId, show, userId]);

    const closeModal = () => {
        paymentMethodsRequestIdRef.current += 1;
        bookingRequestIdRef.current += 1;
        setIsSubmitted(false);
        handleClose();
    };

    const handleWaitForDriver = async () => {
        if (isSubmitted) return;

        if (!cardPaymentAllowed) {
            setError('Credit Card booking is not available in the customer Billing Settings.');
            return;
        }

        if (!show || !pickupId || !userId) return;

        if (!selectedPaymentMethodId) {
            alert('Please select a payment method.');
            return;
        }

        setIsSubmitted(true);
        setError('');
        const requestId = ++bookingRequestIdRef.current;
        const requestedPickupId = pickupId;
        const requestedUserId = userId;
        try {
            const response = await axios.post(`${API_URL}/api/admin/pickups/${requestedPickupId}/waiting-for-driver`, {
                payment_type: 'card',
                payment_method_id: selectedPaymentMethodId,
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const current = currentContextRef.current;
            if (
                requestId !== bookingRequestIdRef.current
                || !current.show
                || !current.cardPaymentAllowed
                || String(current.pickupId) !== String(requestedPickupId)
                || String(current.userId) !== String(requestedUserId)
            ) return;

            handleSuccess(`The delivery ${requestedPickupId} has been successfully marked as waiting for a driver.`, response.data.pickup);
            closeModal();
        }
        catch (requestError) {
            const current = currentContextRef.current;
            if (
                requestId !== bookingRequestIdRef.current
                || !current.show
                || !current.cardPaymentAllowed
                || String(current.pickupId) !== String(requestedPickupId)
                || String(current.userId) !== String(requestedUserId)
            ) return;

            if (requestError.response && requestError.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }
            if (requestError?.response?.data?.errors) {
                const errors = requestError.response.data.errors;
                const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                setError(errorMessages);
            } else if (requestError?.response?.data?.message) {
                setError(requestError.response.data.message);
            } else {
                setError('An error occurred. Please try again later.');
            }
        } finally {
            const current = currentContextRef.current;
            if (
                requestId === bookingRequestIdRef.current
                && current.show
                && current.cardPaymentAllowed
                && String(current.pickupId) === String(requestedPickupId)
                && String(current.userId) === String(requestedUserId)
            ) {
                setIsSubmitted(false);
            }
        }
    };

    return (
        <Modal show={show} onHide={closeModal} size="lg">
            <Modal.Header>
                <Modal.Title>Choose or Add a Credit Card</Modal.Title>
                <button type="button" className="close" onClick={closeModal} aria-label="Close">
                    <i className="fa fa-times"/>
                </button>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="d-flex justify-content-center">
                        <Spinner animation="border" role="status">
                            <span className="sr-only">Loading...</span>
                        </Spinner>
                    </div>
                ) : (
                    <>
                        {success && <div className="alert alert-success">{success}</div>}
                        {error && <div className="alert alert-danger">{error}</div>}
                        <ListGroup>
                            {paymentMethods.map((method, index) => (
                                <ListGroup.Item
                                    key={index}
                                    action
                                    onClick={() => setSelectedPaymentMethodId(method.id)}
                                    active={selectedPaymentMethodId === method.id}
                                    className="d-flex justify-content-between align-items-center"
                                >
                                    <div>
                                        {method.card.brand} **** {method.card.last4} - Expires {method.card.exp_month}/{method.card.exp_year}
                                    </div>
                                    {selectedPaymentMethodId === method.id && (
                                        <i className="fas fa-check" style={{ color: '#28a745' }}/>
                                    )}
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                        {paymentMethods.length === 0 && <p>No payment methods available. Add a new one below.</p>}
                        <hr />
                        <Elements stripe={stripePromise}>
                            <CardForm
                                userId={userId}
                                onCardAdded={fetchPaymentMethods}
                                onError={setError}
                            />
                        </Elements>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="warning"
                    onClick={handleWaitForDriver}
                    disabled={!cardPaymentAllowed || !selectedPaymentMethodId || isSubmitted}
                >
                    {isSubmitted
                        ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />&nbsp;Wait for Driver...</>
                        : "Wait for Driver"
                    }
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default withAuth(CreditCardModal);
